import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../context/userContext";
import { getDefaultRoute } from "../utils/routeUtils";

export function useUserAuth() {
    // Trust localStorage for auth — no server call needed on app open.
    // Expired tokens are handled by the axios interceptor (refresh or logout).
    const token = localStorage.getItem("Token");
    const userId = localStorage.getItem("UserId");
    const isAuthenticated = !!(token && userId);

    return { isAuthenticated, authChecked: true };
}

export function ProtectedRoute({ children, requiredRoleRight, requiredRole, bypassAgreementGate, bypassApplicationGate }) {
    const { user } = useUser();
    const { isAuthenticated, authChecked } = useUserAuth();
    const location = useLocation();

    // Viewer-only users (read-only role) don't have access to the
    // Dashboard. Their landing page is /user/profile instead, and any
    // fallback redirect that would normally point to /dashboard sends
    // them to /user/profile to avoid bouncing between gated routes.
    const userRoles = user?.roles || [];
    const isViewerOnly = userRoles.length > 0 && userRoles.every(r => r === 'Viewer');
    // Same helper the login redirect uses, so a blocked route bounces to the page
    // that role actually lands on. This used to hardcode its own copy, which meant
    // Astern Admin — who can't open the dashboard — was bounced onto it.
    const defaultRoute = getDefaultRoute(userRoles, user?.canCreateSale !== false);

    if (!authChecked) {
        return (
            <div style={{ textAlign: "center", marginTop: "40px" }}>
                <div className="spinner"></div>
                <span>Loading...</span>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // Application-status gate — pin pending applicants to /application-status until approved.
    const isPending = user?.applicationStatus === 'PendingReview' || user?.applicationStatus === 'PendingApproval';
    if (isPending && !bypassApplicationGate) {
        return <Navigate to="/application-status" replace />;
    }
    if (!isPending && location.pathname === '/application-status') {
        return <Navigate to={defaultRoute} replace />;
    }

    // Inactivity gate — backend flags accounts with no sales in the trailing window.
    // /blocked is a PUBLIC route (not wrapped in ProtectedRoute), so no bypass flag
    // or "return to default route" branch is needed here.
    if (user?.accessBlocked) {
        return <Navigate to="/blocked" replace />;
    }

    // First-login agreement gate. Bypass only on the agreement page itself.
    if (user?.mustAcceptAgreement && !bypassAgreementGate) {
        return <Navigate to="/accept-agreement" replace state={{ from: location.pathname }} />;
    }
    if (!user?.mustAcceptAgreement && location.pathname === '/accept-agreement') {
        return <Navigate to={defaultRoute} replace />;
    }

    // ── Training gate (user 2026-09-24) ──────────────────────────────────────
    // Until an agent passes the RSC certification quiz they see the TRAINING
    // ROOM only: tutorials, the quiz, and their own profile. Everything else —
    // calculators, pricing, sales, commission, reports, team — is out of reach,
    // so nobody quotes a customer before they are certified. `canCreateSale` is
    // the same flag the server computes (exempt role, admin override, or a quiz
    // pass) and is re-read here on every navigation. The API enforces the same
    // list server-side (TrainingGateMiddleware); this only saves the round trip.
    //
    // It runs AFTER the application / blocked / agreement gates on purpose. Placed
    // before them it fought the agreement gate: a newly approved agent was sent to
    // /quiz, the agreement gate sent them back to /accept-agreement, and the page
    // rendered blank. The sign-up gates come first; certification second.
    // (user 2026-09-25 — "new ppl registered approved, but this blank")
    const restrictedToTraining = user?.canCreateSale === false;
    // /events belongs here for the same reason it is allowed through the API's own gate
    // and listed in the sidebar: the onboarding training is FOR the people this gate
    // holds back, and a new joiner who cannot reach /events can never attend the session
    // that certifies them. Without it the menu offered Events and this bounced them
    // straight back to /tutorials. (user 2026-10-04, found while testing registration)
    const TRAINING_PATHS = ['/tutorials', '/quiz', '/events', '/support', '/user/profile', '/accept-agreement', '/application-status', '/blocked'];
    if (restrictedToTraining && !TRAINING_PATHS.some(p => location.pathname.startsWith(p))) {
        // To TUTORIALS, not the quiz. getDefaultRoute already lands a trainee on
        // /tutorials "because that is what the quiz examines" - but this bounce sent
        // them to /quiz instead, so every blocked link dumped them on the exam and it
        // read as being unable to reach the tutorials at all. The two agree now.
        // (user 2026-09-27: "they keep redirect to quiz. they cant access tutorial")
        return <Navigate to="/tutorials" replace />;
    }

    // Viewer-only users hitting /dashboard get bounced to their landing page.
    if (isViewerOnly && location.pathname === '/dashboard') {
        return <Navigate to="/user/profile" replace />;
    }

    if (requiredRoleRight && Array.isArray(requiredRoleRight)) {
        const hasAccess = requiredRoleRight.some(right => user?.roleRights?.includes(right));

        if (!hasAccess) {
            // return <NotFound />;
            return <Navigate to={defaultRoute} replace />;
        }
    }

    if (requiredRole && Array.isArray(requiredRole)) {
        const hasRole = requiredRole.some(role => user?.roles?.includes(role));
        if (!hasRole) {
            return <Navigate to={defaultRoute} replace />;
        }
    }

    // if (requiredRight) {
    //     // User rights should now be available from Context (loaded from LocalStorage)
    //     const userRights = user?.roleRights || [];

    //     const hasRight = Array.isArray(requiredRight)
    //         ? requiredRight.some(r => userRights.includes(r))
    //         : userRights.includes(requiredRight);
    //     if (!hasRight) {
    //         return <NotFound />;
    //     }
    // }

    return children;
    // return isAuthenticated ? children : <Navigate to="/" replace />;



    // if (Array.isArray(requiredRoles) && requiredRoles.length > 0) {
    //     hasAccess = requiredRoles.some(role => userRoles.includes(role));
    //     if (isAuthenticated && !hasAccess) {
    //         return <Navigate to="/sales" replace />;
    //     }
    // }
}